import type { ComponentType } from 'react'
import {
  AppWindow,
  Settings,
  Settings2,
  Shield,
  FileText,
  FolderOpen,
  BotMessageSquare,
  Brain,
  Library,
  GraduationCap,
  Presentation,
  CheckCircle,
  Building2,
  Bot,
  ClipboardCheck,
} from 'lucide-react'
import manifest from '../../deploy/apps.manifest.json'

export type AppCategory = 'system' | 'multimedia' | 'productivity' | 'admin'

export type AppId =
  | 'app-store'
  | 'settings'
  | 'widget-settings'
  | 'cortex'
  | 'communicator2'
  | 'ai-knowledge-extractor'
  | 'knowledge-verifier'
  | 'cdb-sessions'
  | 'cdb-training'
  | 'prompt-library'
  | 'file-preview'
  | 'drive2'
  | 'collabora-editor'
  | 'wissenshaus'
  | 'harness'
  | 'cdb-control'

export type AppDef = {
  id: AppId
  Icon: ComponentType<{ className?: string }>
  system?: boolean // System-Apps sind gesch�tzt (nicht deinstallierbar)
  category?: AppCategory
  adminOnly?: boolean // Nur f�r Administratoren verf�gbar
  supervisorVisible?: boolean // Auch f�r Supervisors verf�gbar
  implemented?: boolean // App ist vollst�ndig implementiert
  hideFromMenu?: boolean // App wird nicht im Start Men� angezeigt (nur programmatisch �ffenbar)
  hideFromStore?: boolean // App wird nicht im App Store angezeigt
  externalUrl?: string // Wenn gesetzt, wird die URL in einem neuen Tab ge�ffnet statt eines Fensters
}

/**
 * Apps removed from the registry (#673): CDB Drive (successor: cdbDrive) and
 * the CDB Knowledge Communicator (successor: cdbChat). Their ids survive in
 * persisted window states and in old deep links, both of which would
 * otherwise reopen an app the list no longer offers. Not aliased to the
 * successors — the persisted params belong to the old app.
 */
export const RETIRED_APP_IDS: ReadonlySet<string> = new Set(['drive', 'ai-chat'])

/** Each app's icon; the type makes a missing one a compile error. */
const ICONS: Record<AppId, AppDef['Icon']> = {
  'app-store': AppWindow,
  settings: Settings,
  'widget-settings': Settings2,
  cortex: Shield,
  communicator2: BotMessageSquare,
  'collabora-editor': FileText,
  'ai-knowledge-extractor': Brain,
  'knowledge-verifier': CheckCircle,
  'cdb-sessions': GraduationCap,
  'cdb-training': Presentation,
  'prompt-library': Library,
  'file-preview': FileText,
  wissenshaus: Building2,
  harness: Bot,
  'cdb-control': ClipboardCheck,
  drive2: FolderOpen,
}

type ManifestApp = {
  id: AppId
  kind: 'system' | 'admin' | 'assignable'
  category: AppCategory
  adminOnly?: boolean
  supervisorVisible?: boolean
  hideFromMenu?: boolean
  hideFromStore?: boolean
  aliases?: string[]
}

/**
 * The built-in apps, in start-menu order, from `deploy/apps.manifest.json`
 * (#1647) — the same list the BFF and the migrate job read. Edit apps there;
 * this file only adds the icons.
 */
export const APPS: AppDef[] = (manifest.apps as ManifestApp[]).map((app) => ({
  id: app.id,
  Icon: ICONS[app.id],
  // Cortex counts as a system app here; the BFF checks the admin role for it.
  system: app.kind !== 'assignable',
  category: app.category,
  adminOnly: app.adminOnly,
  supervisorVisible: app.supervisorVisible,
  implemented: true,
  hideFromMenu: app.hideFromMenu,
  hideFromStore: app.hideFromStore,
}))

/**
 * Old app ids and the app they open now (`admin` → cortex, `pdf-viewer` →
 * file-preview, …), for deep links and saved windows. The BFF keeps the same
 * names reserved (`BUILTIN_APPS`).
 */
export const APP_ID_ALIASES: Readonly<Record<string, AppId>> = Object.fromEntries(
  (manifest.apps as ManifestApp[]).flatMap((app) => (app.aliases ?? []).map((alias) => [alias, app.id])),
)

// Hilfsfunktion: Nur implementierte Apps (ohne App Store selbst)
export const getImplementedApps = (): AppDef[] => {
  return APPS.filter((app) => app.implemented === true && app.id !== 'app-store' && !app.hideFromStore)
}

// Hilfsfunktion: Apps mit Benutzer-Berechtigungen filtern
export const getAppsForUser = async (
  userAssignedApps: string[] = [],
  userRole: 'admin' | 'user' = 'user',
  isSupervisor: boolean = false
): Promise<AppDef[]> => {
  return APPS.filter((app) => {
    // Nur implementierte Apps anzeigen
    if (!app.implemented) return false

    // Apps die nicht im Menü angezeigt werden sollen filtern
    if (app.hideFromMenu) return false

    // System-Apps (z. B. settings, app-store) sind immer verf�gbar
    if (app.system) return true

    // Admin-Apps: f�r Admins und Supervisors (wenn supervisorVisible)
    if (app.adminOnly) {
      if (userRole === 'admin') return true
      if (isSupervisor && app.supervisorVisible) return true
      return false
    }

    // Normale Apps: Benutzer muss Admin sein oder die App zugewiesen haben
    return userRole === 'admin' || userAssignedApps.includes(app.id)
  })
}

// Hilfsfunktion: Nur Apps, die vom Admin zugewiesen werden k�nnen (keine System-Apps, keine Admin-Apps)
export const getAssignableApps = (): AppDef[] => {
  return APPS.filter((app) => {
    // Nur implementierte Apps
    if (!app.implemented) return false

    // Keine System-Apps (settings, app-store, ai-chat)
    if (app.system) return false

    // Keine Admin-Apps (admin)
    if (app.adminOnly) return false

    // Nur normale Apps, die zugewiesen werden k�nnen
    return true
  })
}

// Hilfsfunktion: Pr�ft, ob eine App eine System-App ist
export const isSystemApp = (appId: string): boolean => {
  const app = APPS.find((a) => a.id === appId)
  return app?.system === true
}

// Hilfsfunktion: Pr�ft, ob eine App eine Admin-App ist
export const isAdminApp = (appId: string): boolean => {
  const app = APPS.find((a) => a.id === appId)
  return app?.adminOnly === true
}
